/* eslint-disable indent */
import { keyBy } from 'lodash';
import moment from 'moment';
import { titleCase } from 'utils/common';
import { PLACEHOLDER_TEXT } from 'utils/constants';
import {
  BREAKDOWN_EVENT,
  BREAKDOWN_EVENT_FOR_CHARGES,
  SETTLEMENT_STATUS_TYPES,
  SETTLEMENT_TIMELINE,
  SETTLEMENT_TIMELINE_TRANSFER_STATE,
  VENDOR_SETTLEMENT_STATUS_TYPES,
} from './constants';

const settlementStatus = [
  SETTLEMENT_STATUS_TYPES.PENDING,
  VENDOR_SETTLEMENT_STATUS_TYPES.CREATED,
  VENDOR_SETTLEMENT_STATUS_TYPES.PENDING,
];

export const formatInstantSettlementsDailyLimit = amount => {
  let limit = amount || 0;
  let suffix = '';

  if (limit < 1) {
    limit = amount * 100000;
  } else {
    suffix = 'lakhs';
  }
  return { limit, suffix };
};

export const formatDateToDateTimeFormat = (dateString, format) => {
  if (!dateString || !format) {
    return PLACEHOLDER_TEXT;
  }
  return moment(dateString).format(format);
};

export const checkInProgressTimeline = timelineData => {
  if (timelineData.currentStatus !== SETTLEMENT_STATUS_TYPES.PENDING) {
    return {};
  }

  const timelineByTransferState = keyBy(timelineData.timeLine, 'transferState');

  let currentState = {};

  if (
    timelineByTransferState[
      SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_BANK
    ]
  ) {
    currentState = {
      YOUR_BANK: 'IN_PROGRESS',
      [SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_BANK]: 'SUCCESS',
      [SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_CASHFREE]: 'SUCCESS',
    };
  } else if (
    timelineByTransferState[
      SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_CASHFREE
    ]
  ) {
    currentState = {
      YOUR_BANK: 'PENDING',
      [SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_BANK]: 'IN_PROGRESS',
      [SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_CASHFREE]: 'SUCCESS',
    };
  } else {
    currentState = {
      YOUR_BANK: 'PENDING',
      [SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_BANK]: 'PENDING',
      [SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_CASHFREE]: 'IN_PROGRESS',
    };
  }

  return currentState;
};

/*
    processes the timeline response and orders it for rendering
  */
export const processTimelineResponse = (response, options = {}) => {
  const [timelineData] = response;
  if (!timelineData) {
    return {};
  }
  const processedTimeline = [];
  const { hideRetrials = false, reverse = false } = options;

  const isInProgress =
    timelineData.currentStatus === SETTLEMENT_STATUS_TYPES.PENDING;
  const inProgressState = checkInProgressTimeline(timelineData);

  const timelineByTransferState = keyBy(timelineData.timeLine, 'transferState');

  let categoryNumber = 0;

  if (timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.SUCCESS]) {
    processedTimeline.push({
      ...timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.SUCCESS],
      ...SETTLEMENT_TIMELINE.YOUR_BANK,
      categoryNumber,
    });
  } else if (
    timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.FAILED]
  ) {
    processedTimeline.push({
      ...timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.FAILED],
      ...SETTLEMENT_TIMELINE.YOUR_BANK,
      categoryNumber,
    });
  } else {
    processedTimeline.push({
      ...SETTLEMENT_TIMELINE.YOUR_BANK,
      status: inProgressState.YOUR_BANK,
      categoryNumber,
    });
  }

  categoryNumber += 1;
  processedTimeline.push({
    ...(timelineByTransferState[
      SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_BANK
    ] || {}),
    ...SETTLEMENT_TIMELINE.INITIATED_BY_BANK,
    ...(isInProgress &&
    !timelineByTransferState[
      SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_BANK
    ]
      ? // eslint-disable-next-line prettier/prettier, indent
        {
          status:
            inProgressState[
              SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_BANK
            ],
        }
      : {}),
    categoryNumber,
  });

  if (!hideRetrials) {
    if (timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.RETRY_3]) {
      processedTimeline.push({
        ...timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.RETRY_3],
        ...SETTLEMENT_TIMELINE.RETRY,
        retrialNumber: 3,
        categoryNumber,
      });
    }

    if (timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.RETRY_2]) {
      processedTimeline.push({
        ...timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.RETRY_2],
        ...SETTLEMENT_TIMELINE.RETRY,
        retrialNumber: 2,
        categoryNumber,
      });
    }

    if (timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.RETRY_1]) {
      processedTimeline.push({
        ...timelineByTransferState[SETTLEMENT_TIMELINE_TRANSFER_STATE.RETRY_1],
        ...SETTLEMENT_TIMELINE.RETRY,
        retrialNumber: 1,
        categoryNumber,
      });
    }
  }

  categoryNumber += 1;
  processedTimeline.push({
    ...(timelineByTransferState[
      SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_CASHFREE
    ] || {}),
    ...SETTLEMENT_TIMELINE.INITIATED_BY_CASHFREE,
    ...(isInProgress &&
    !timelineByTransferState[
      SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_CASHFREE
    ]
      ? // eslint-disable-next-line prettier/prettier, indent
        {
          status:
            inProgressState[
              SETTLEMENT_TIMELINE_TRANSFER_STATE.INITIATED_BY_CASHFREE
            ],
        }
      : {}),
    categoryNumber,
  });

  return {
    ...timelineData,
    timeLine: reverse ? processedTimeline.reverse() : processedTimeline,
  };
};

export const getTimelineItemStatus = (
  timelineItem,
  isSettlementDetailStatus,
) => {
  if (
    ['SUCCESS', 'PAID', SETTLEMENT_STATUS_TYPES.PROCESSED].includes(
      timelineItem.status,
    )
  ) {
    return {
      className: 'success',
      color: 'success',
      text: 'Success',
    };
  }

  if (timelineItem.status === 'FAILED') {
    return {
      className: 'failed',
      color: 'danger',
      text: 'Failed',
    };
  }
  if (timelineItem.status === 'REVERSED') {
    return {
      className: 'failed',
      color: 'danger',
      text: 'Reversed',
    };
  }

  if (
    timelineItem.status === 'IN_PROGRESS' ||
    (isSettlementDetailStatus && settlementStatus.includes(settlementStatus))
  ) {
    return {
      className: 'progress',
      color: 'warning',
      text: 'In Progress',
    };
  }

  if (timelineItem.status === 'PENDING' || timelineItem.status === 'CREATED') {
    return {
      className: 'skipped',
      color: 'bodyLight',
      text: 'Pending',
    };
  }

  if (
    timelineItem.status === SETTLEMENT_STATUS_TYPES.SKIPPED &&
    isSettlementDetailStatus
  ) {
    return {
      className: 'skipped',
      color: 'bodyLight',
      text: 'Skipped',
    };
  }

  return {
    className: 'display-none',
  };
};

export const getSmartReconEventLabel = event =>
  BREAKDOWN_EVENT[event] || titleCase(event);

export const getChargesEventLabel = event =>
  BREAKDOWN_EVENT_FOR_CHARGES[event] || titleCase(event);

export const getChargesDetails = settlementData => {
  if (!settlementData?.chargesDetailedView?.chargesComponents?.length) {
    return {
      prepaidData: [],
      postpaidData: [],
      netCharges: 0,
    };
  }
  const prepaidData = settlementData.chargesDetailedView.chargesComponents.filter(
    charge => charge.chargeType === 'PREPAID',
  );
  const postpaidData = settlementData.chargesDetailedView.chargesComponents.filter(
    charge => charge.chargeType === 'POSTPAID',
  );
  return {
    prepaidData,
    postpaidData,
    netCharges: settlementData.chargesDetailedView.netCharges,
  };
};

export const sortSettlementComponentsList = settlementComponentsList => {
  if (!settlementComponentsList?.length) {
    return [];
  }
  const otherAdjustmentComponentType = 'OTHER_ADJUSTMENT';

  const [otherAdjustmentComponent] = settlementComponentsList.filter(
    settlementComponent =>
      settlementComponent.event === otherAdjustmentComponentType,
  );
  if (!otherAdjustmentComponent) {
    return settlementComponentsList;
  }

  const processedSettlementComponents = settlementComponentsList.filter(
    settlementComponent =>
      settlementComponent.event !== otherAdjustmentComponentType,
  );
  processedSettlementComponents.push(otherAdjustmentComponent);
  return processedSettlementComponents;
};

export const updateHeadersWithTimezone = (columns, timezone) =>
  columns.map(column =>
    column.accessorKey === 'initiatedOn' ||
    column.accessorKey === 'transactionPeriod'
      ? { ...column, header: `${column.header} (${timezone})` }
      : column,
  );
